import {
  createFileRoute,
  Link,
  type MakeRouteMatchUnion,
} from "@tanstack/react-router";
import { useContext, useMemo } from "react";
import {
  AppError,
  Button,
  EmptyState,
  Loader,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeaderCell,
  TableRow,
  Tag,
  type TagTone,
} from "@bfc-software/pallet";
import { TFunction } from "i18next";
import { AuthContext, AuthUtils, isDefined } from "@bfc-software/app-toolbox";
import {
  useTranslation,
  TRANSLATION_NAMESPACE,
} from "@bfc-software/bfc-web-ui/i18n";
import { apiClient } from "@bfc-software/bfc-web-ui/lib/task-planner-api-client";
import {
  type PageSearch,
  pageQuery,
  validatePageSearch,
} from "@bfc-software/bfc-web-ui/lib/cursor-pagination";
import { ListPager } from "@bfc-software/bfc-web-ui/ui/shared/list-pager";
import { DateValue } from "@bfc-software/bfc-web-ui/ui/shared/date-value";
import palletOverrides from "@bfc-software/bfc-web-ui/pallet-overrides.module.css";

import {
  type TaskActivationStatus,
  type TaskActivationSummary,
} from "./-components/types";
import styles from "./index.module.css";

type SelectorTaskSearch = PageSearch;

/**
 * Rows per request.
 *
 * Held here rather than shared, because the server derives `page_number` and
 * `page_count` from the limit of the request that produced them -- so the limit
 * is part of what a page ordinal means, and a route that changes it renumbers
 * its own pages and nobody else's. Not user-adjustable: page size in the URL
 * would renumber pages under the reader mid-walk.
 */
const PAGE_SIZE = 25;

export const Route = createFileRoute(
  "/$lang/_layout/orgs/$org/_layout/internal/whs/$location/_layout/selector-tasks/$selectorId/",
)({
  validateSearch: (search?: Record<string, unknown>): SelectorTaskSearch =>
    validatePageSearch(search),
  staticData: {
    // Nothing above this route renders the sessions list, so the trail back to
    // it is authored here rather than inherited from a parent match.
    crumb: (match: MakeRouteMatchUnion, t: TFunction) => {
      const params = match.params as {
        lang?: string;
        org?: string;
        location?: string;
      };
      return [
        {
          label: t("page.selectorSessions.crumb"),
          to: "/$lang/orgs/$org/internal/whs/$location/selector-sessions",
          params: {
            lang: params.lang ?? "",
            org: params.org ?? "",
            location: params.location ?? "",
          },
        },
        { label: t("page.selectorTasks.crumb") },
      ];
    },
  },
  component: RouteComponent,
});

const STATUS_TONES: Record<TaskActivationStatus, TagTone> = {
  pending: "neutral",
  assigned: "info",
  claimed: "warning",
  completed: "success",
};

function ActivationRow({
  activation,
  lang,
  org,
  location,
}: {
  activation: TaskActivationSummary;
  lang: string;
  org: string;
  location: string;
}) {
  return (
    <TableRow data-test-role="task-activation-row">
      <TableCell>
        <span
          className={styles.truncatedId}
          title={activation.activation_id}
          data-test-role="activation-id"
        >
          {activation.activation_id}
        </span>
      </TableCell>
      <TableCell>
        <Tag
          label={activation.status}
          display={{ tone: STATUS_TONES[activation.status] }}
        />
      </TableCell>
      <TableCell>
        <Link
          to="/$lang/orgs/$org/internal/whs/$location/task-plans/$task-plan"
          params={{
            "task-plan": activation.task_plan_id,
            lang,
            org,
            location,
          }}
          className={styles.truncatedId}
          title={activation.task_plan_id}
        >
          {activation.task_plan_id}
        </Link>
      </TableCell>
      <TableCell>
        {/*
          Straight to the picks. Note the inverted naming against TaskSummary:
          here `task_id` is the ULID and `task_internal_id` is the numeric,
          warehouse-scoped id -- and it is the numeric one the $task route
          keys on.
        */}
        <Link
          to="/$lang/orgs/$org/internal/whs/$location/task-plans/$task-plan/$task"
          params={{
            "task-plan": activation.task_plan_id,
            task: `${activation.task_internal_id}`,
            lang,
            org,
            location,
          }}
          data-test-role="activation-task-link"
        >
          {activation.task_internal_id}
        </Link>
      </TableCell>
      <TableCell>{activation.sub_type}</TableCell>
      <TableCell>
        {isDefined(activation.claimed_at) ? (
          <DateValue dateValue={activation.claimed_at} />
        ) : (
          <span className={styles.emptyValue}>—</span>
        )}
      </TableCell>
      <TableCell>
        {isDefined(activation.completed_at) ? (
          <DateValue dateValue={activation.completed_at} />
        ) : (
          <span className={styles.emptyValue}>—</span>
        )}
      </TableCell>
    </TableRow>
  );
}

export function RouteComponent() {
  const { lang, org, location, selectorId } = Route.useParams();
  const page = Route.useSearch();
  const navigate = Route.useNavigate();
  const { state: authState } = useContext(AuthContext);
  const { t } = useTranslation({
    lang,
    namespace: TRANSLATION_NAMESPACE.warehouse,
  });

  const client = useMemo(() => apiClient(), []);

  const orgId = AuthUtils.getOrgIdByStub(authState, org) ?? org;
  const tenantId = AuthUtils.getTenantId(authState, orgId, location);

  const enabled = typeof orgId === "string";

  /*
    The route's `$selectorId` is the selector's AAA app-user id
    (`external_user_id`), not the `selector_profile.id` ULID -- which is what the
    listing's `selectorAppUserExtIds` filter takes, and the only direction the
    service offers: `/selectors` resolves an AAA id to a ULID, never the reverse.
    The sessions list is the one way in here and it holds both ids, so it links
    with the AAA one; the ULID is resolved off the rows below, for display.

    Named for the URL segment rather than for what it holds, because renaming the
    segment regenerates the route tree.
  */
  const query = client.useQuery(
    "get",
    "/{orgId}/task-activations",
    {
      params: {
        path: { orgId: orgId! },
        query: {
          selectorAppUserExtIds: [selectorId],
          warehouseExtId: location,
          // Live rows only -- what this selector holds now, not superseded
          // assignment history.
          live: true,
          limit: PAGE_SIZE,
          ...pageQuery(page),
        },
      },
    },
    { enabled },
  );

  const activations = query.data?.task_activations ?? [];

  // The ULID behind the AAA id in the URL, resolved from the rows rather than by
  // a second call: `selector_id` is on the wire precisely so a listing is
  // attributable. With no rows there is nothing to resolve, and the heading then
  // stands alone.
  const selectorProfileId = activations.find((activation) =>
    isDefined(activation.selector_id),
  )?.selector_id;

  return (
    <div
      className={palletOverrides.basicPageContent}
      data-tenant-id={tenantId}
      data-org-id={orgId}
    >
      <div className={palletOverrides.basicPageActionArea}>
        <div className={palletOverrides.basicPageActionAreaLeft}>
          <div className={styles.selectorHeading}>
            <h1 data-test-role="selector-tasks-heading">{selectorId}</h1>
            {isDefined(selectorProfileId) ? (
              <span className={styles.selectorId} title={selectorProfileId}>
                {selectorProfileId}
              </span>
            ) : null}
          </div>

          <ListPager
            pagination={query.data?.pagination}
            isFetching={query.isFetching}
            onChange={(next) =>
              navigate({
                search: (prev) => ({
                  ...prev,
                  cursor: next.cursor,
                  dir: next.dir,
                }),
              })
            }
            t={t}
          />
        </div>
        <div className={palletOverrides.basicPageActionAreaRight}>
          <div className={styles.headerActions}>
            {/*
              The sessions listing filters on the ULID, so the trip back can only
              be narrowed once a row has given us one. With no rows there is no
              ULID to send, and the link goes to the unfiltered listing rather
              than to one filtered by an AAA id it would silently ignore.
            */}
            <Link
              to="/$lang/orgs/$org/internal/whs/$location/selector-sessions"
              params={{ lang, org, location }}
              search={
                isDefined(selectorProfileId)
                  ? { selectorId: selectorProfileId }
                  : {}
              }
              className={palletOverrides.linkButton}
              data-test-role="view-sessions"
            >
              {t("page.selectorTasks.viewSessions")}
            </Link>
            <Button
              variant="tertiary"
              text={t("page.selectorTasks.refresh")}
              isDisabled={query.isFetching}
              onPress={() => query.refetch()}
              data-test-role="selector-tasks-refresh"
            />
          </div>
        </div>
      </div>
      {query.isLoading || !enabled ? (
        <Loader type="page" />
      ) : query.isError ? (
        <AppError
          description={t("page.selectorTasks.failed")}
          resolution={t("page.selectorTasks.failureSupport")}
        />
      ) : (
        <Table
          aria-label={t("page.selectorTasks.table.ariaLabel")}
          className={
            activations.length < 1 ? palletOverrides.fullHeightTable : ""
          }
          isResizable={false}
        >
          <TableHead>
            <TableHeaderCell isRowHeader={true}>
              {t("page.selectorTasks.table.activation")}
            </TableHeaderCell>
            <TableHeaderCell>
              {t("page.selectorTasks.table.status")}
            </TableHeaderCell>
            <TableHeaderCell>
              {t("page.selectorTasks.table.plan")}
            </TableHeaderCell>
            <TableHeaderCell>
              {t("page.selectorTasks.table.task")}
            </TableHeaderCell>
            <TableHeaderCell>
              {t("page.selectorTasks.table.subType")}
            </TableHeaderCell>
            <TableHeaderCell>
              {t("page.selectorTasks.table.claimed")}
            </TableHeaderCell>
            <TableHeaderCell>
              {t("page.selectorTasks.table.completed")}
            </TableHeaderCell>
          </TableHead>
          <TableBody>
            {activations.length === 0 ? (
              <TableRow>
                <TableCell colSpan={7}>
                  <EmptyState heading={t("page.selectorTasks.empty")} />
                </TableCell>
              </TableRow>
            ) : (
              activations.map((activation) => (
                <ActivationRow
                  key={activation.activation_id}
                  activation={activation}
                  lang={lang}
                  org={org}
                  location={location}
                />
              ))
            )}
          </TableBody>
        </Table>
      )}
    </div>
  );
}
